import React, { useEffect, useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, CheckCircle2, ChevronRight, Landmark, Building2 } from "lucide-react";
import { siteData, realImages } from "../mock";
import { CITIES } from "../data/specialties";
import { CITY_DETAILS } from "../data/cityDetails";
import { cityUrl } from "../data/urls";
import SEO from "../components/SEO";
import CTASection from "../components/CTASection";
import RelatedArticles from "../components/RelatedArticles";
import CityServiceLinks from "../components/CityServiceLinks";
import JsonLd from "../components/JsonLd";
import { cityMeta } from "../data/pageMeta";
import { breadcrumbSchema, serviceSchema } from "../data/schema";

export default function CityPage({ slug: slugProp }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const params = useParams();
  const ville = slugProp || params.ville;
  const city = useMemo(() => CITIES.find((c) => c.slug === ville), [ville]);
  const details = useMemo(() => (city ? CITY_DETAILS[city.slug] : null), [city]);
  const otherCities = useMemo(
    () => (city ? CITIES.filter((c) => c.slug !== ville) : []),
    [ville, city]
  );

  if (!city) return <Navigate to="/" replace />;

  const cityName = city.short || city.name;
  const { title: metaTitle, description: metaDescription } = cityMeta(city.slug);
  const jsonLd = [
    serviceSchema({
      name: `Fasciathérapie et coach sport-santé à domicile à ${cityName}`,
      serviceType: ["Fasciathérapie", "Coach sportif à domicile", "Sport santé"],
      description: metaDescription,
      areaServed: [cityName],
    }),
    breadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: cityName, path: cityUrl(city.slug) },
    ]),
  ];

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        keywords={[
          `fasciathérapie ${cityName}`,
          `coach sportif ${cityName}`,
          `sport santé ${cityName}`,
          `mal de dos ${cityName}`,
          "Tissual Balancing",
          "à domicile",
          ...(details?.districts?.slice(0, 3) || []).map((d) => `${cityName} ${d}`),
        ]}
      />
      <JsonLd data={jsonLd} />

      <div className="container-tight pt-6">
        <nav className="flex items-center gap-2 text-xs text-[#8a7d6a]" aria-label="Fil d'ariane" data-testid="city-breadcrumb">
          <Link to="/" className="hover:text-[#c76d4b]">Accueil</Link>
          <ChevronRight size={12} />
          <span className="text-[#2b2620] font-medium">{cityName}</span>
        </nav>
      </div>

      <section className="container-tight py-10 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="chip mb-5"><MapPin size={12} /> Intervention à {cityName} ({city.cp})</span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium text-[#2b2620] tracking-tight leading-[1.05]">
            Fasciathérapie et coach sportif à {cityName}
          </h1>
          {details?.tagline && (
            <p className="mt-3 font-serif text-2xl text-[#c76d4b] italic">{details.tagline}</p>
          )}
          <p className="mt-6 text-lg text-[#5c554c] leading-relaxed">
            {details?.intro || `Basé à Saint-Orens, j'interviens à domicile à ${cityName} pour vous accompagner en fasciathérapie et coaching sport santé.`}
          </p>
          <div className="mt-8 flex gap-3 flex-wrap">
            <a
              href="https://www.resalib.fr/praticien/120570-jean-christophe-happillon-fasciatherapeute-quint-fonsegrives"
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
            >
              Réserver une séance <ArrowRight size={16} />
            </a>
            <a href={`tel:${siteData.practitioner.phone.replace(/\s/g, "")}`} className="btn-outline">
              <Phone size={16} /> {siteData.practitioner.phone}
            </a>
          </div>
        </div>
        <div className="rounded-[2rem] overflow-hidden aspect-[4/3] lg:aspect-[4/5]">
          <img src={realImages.hero} alt={`Fasciathérapie à ${cityName}`} className="w-full h-full object-cover" loading="lazy" />
        </div>
      </section>

      {/* Districts served */}
      {details?.districts && (
        <section className="container-tight py-12">
          <div className="max-w-3xl mb-8">
            <span className="chip mb-3"><Building2 size={12} /> Quartiers desservis</span>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2b2620] tracking-tight leading-tight">
              Je me déplace dans tous les quartiers de {cityName}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {details.districts.map((d) => (
              <span key={d} className="px-4 py-2 rounded-full bg-white border border-[#eee5d6] text-sm text-[#5c554c]">
                <MapPin size={12} className="inline mr-1 text-[#c76d4b]" /> {d}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Local landmarks / points of interest */}
      {details?.landmarks && (
        <section className="bg-gradient-to-br from-[#f5ead9] to-[#fce8d5] py-16">
          <div className="container-tight">
            <div className="max-w-3xl mb-10">
              <span className="chip mb-3"><Landmark size={12} /> Points de repère</span>
              <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2b2620] tracking-tight leading-tight">
                {cityName} et ses lieux clés
              </h2>
              <p className="mt-3 text-[#5c554c]">Des repères familiers pour situer mes interventions et adapter chaque accompagnement à votre contexte.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {details.landmarks.map((l) => (
                <div key={l.name} className="bg-white rounded-2xl border border-[#eee5d6] p-6">
                  <h3 className="font-serif text-lg font-medium text-[#2b2620] mb-2">{l.name}</h3>
                  <p className="text-sm text-[#5c554c] leading-relaxed">{l.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services */}
      <section className="container-tight py-14">
        <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2b2620] tracking-tight text-center mb-10">
          Fasciathérapie et coach sport-santé à domicile à {cityName}
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/fasciathérapie-toulouse" className="group bg-white rounded-3xl border border-[#eee5d6] p-8 hover:border-[#c76d4b]/40 hover:shadow-xl transition-all">
            <div className="aspect-[16/10] rounded-2xl overflow-hidden mb-5">
              <img src={realImages.fasciaKnee} alt={`Fasciathérapie ${cityName}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
            </div>
            <span className="chip mb-3">Méthode Tissual Balancing</span>
            <h3 className="font-serif text-2xl font-medium text-[#2b2620] mb-3 group-hover:text-[#c76d4b] transition-colors">Fasciathérapie à {cityName}</h3>
            <p className="text-[#5c554c] leading-relaxed">Une approche douce pour soulager en profondeur les douleurs chroniques (dos, cervicales, tendinites, sciatique) directement chez vous.</p>
            <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#c76d4b] group-hover:gap-3 transition-all">En savoir plus <ArrowRight size={14} /></div>
          </Link>

          <Link to="/coach-domicile" className="group bg-white rounded-3xl border border-[#eee5d6] p-8 hover:border-[#c76d4b]/40 hover:shadow-xl transition-all">
            <div className="aspect-[16/10] rounded-2xl overflow-hidden mb-5">
              <img src={realImages.strengthMedball} alt={`Coach sportif ${cityName}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
            </div>
            <span className="chip mb-3">50% crédit d'impôt</span>
            <h3 className="font-serif text-2xl font-medium text-[#2b2620] mb-3 group-hover:text-[#c76d4b] transition-colors">Coach sportif à {cityName}</h3>
            <p className="text-[#5c554c] leading-relaxed">Sport santé, reprise après blessure, remise en forme adaptée : programme personnalisé à domicile avec suivi rigoureux.</p>
            <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#c76d4b] group-hover:gap-3 transition-all">En savoir plus <ArrowRight size={14} /></div>
          </Link>
        </div>
      </section>

      <CityServiceLinks cityName={cityName} />

      {/* Why here */}
      {details?.why && (
        <section className="container-tight py-14">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2b2620] tracking-tight mb-6">
              Pourquoi un praticien local à {cityName} ?
            </h2>
            <ul className="space-y-4 text-[#5c554c]">
              {details.why.map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#c76d4b] flex-shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Local FAQ */}
      {details?.faq && details.faq.length > 0 && (
        <section className="container-tight py-14 max-w-3xl">
          <h2 className="font-serif text-3xl font-medium text-[#2b2620] mb-8">
            Questions fréquentes à {cityName}
          </h2>
          <div className="space-y-3">
            {details.faq.map((f) => (
              <details key={f.q} className="bg-white rounded-2xl border border-[#eee5d6] p-5 group">
                <summary className="font-serif text-lg font-medium text-[#2b2620] cursor-pointer list-none flex items-center justify-between">
                  {f.q}
                  <ChevronRight size={18} className="text-[#c76d4b] group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-4 text-[#5c554c] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Other cities */}
      <section className="container-tight py-14">
        <h2 className="font-serif text-2xl font-medium text-[#2b2620] mb-6">Autres villes desservies</h2>
        <div className="flex flex-wrap gap-2">
          {otherCities.map((c) => (
            <Link key={c.slug} to={cityUrl(c.slug)} className="px-4 py-2 rounded-full bg-white border border-[#eee5d6] text-sm text-[#5c554c] hover:border-[#c76d4b] hover:text-[#c76d4b] transition-colors">
              <MapPin size={12} className="inline mr-1" /> {c.short || c.name}
            </Link>
          ))}
        </div>
      </section>

      <RelatedArticles title={`À lire pour les habitants de ${cityName}`} />
      <CTASection />
    </>
  );
}
